// Outputs: the files a workflow produces.
//
//   {
//     id: "output1", fileName: "weekly_summary", format: "csv" | "xlsx",
//     dateFormat?: "yyyy-MM-dd",                 // how dates are written to CSV
//     sheets: [{
//       name: "Summary", from: "step3", writeHeader: true,
//       columns?: [{ position: 1, header: "Freelancer", column: "Freelancer" }]
//     }]
//   }
// Without `columns`, every column of `from` is written in order. Positions
// may leave gaps (e.g. 1 and 3), which stay empty. CSV outputs use the first
// sheet only.
import Papa from "papaparse";
import { tableLabel } from "./runner";
import { columnNames, hasColumn } from "./table";
import { formatDate, isBlank, isValidDate, toExcelDate } from "./values";

const sheetsOf = (output) => output.sheets || [{ name: "Sheet1", from: output.from, columns: output.columns, writeHeader: output.writeHeader }];

const sheetColumns = (sheet, table) =>
  sheet.columns?.length
    ? sheet.columns
    : columnNames(table).map((column, index) => ({ position: index + 1, header: column, column }));

// Builds { name, rows } (a 2D array, header first) for one sheet.
const buildSheet = (sheet, table) => {
  const columns = sheetColumns(sheet, table);
  const width = columns.reduce((max, c) => Math.max(max, Number(c.position)), 0);
  const rows = [];
  if (sheet.writeHeader !== false) {
    const header = new Array(width).fill(null);
    columns.forEach((c) => {
      header[c.position - 1] = c.header ?? c.column ?? null;
    });
    rows.push(header);
  }
  table.rows.forEach((row) => {
    const line = new Array(width).fill(null);
    columns.forEach((c) => {
      if (c.column) line[c.position - 1] = row[c.column] ?? null;
    });
    rows.push(line);
  });
  return { name: sheet.name || "Sheet1", rows };
};

const validateSheet = (sheet, table, label, workflow) => {
  const errors = [];
  if (!sheet.from) return [`${label}: choose which source or step to write`];
  if (!table) {
    const exists = [...(workflow.sources || []), ...(workflow.steps || [])].some((item) => item.id === sheet.from);
    return [exists ? `${label}: ${tableLabel(workflow, sheet.from)} has an error, so there's nothing to write yet` : `${label}: not connected. Link a source or step to this output.`];
  }
  const positions = new Set();
  sheetColumns(sheet, table).forEach((c) => {
    const position = Number(c.position);
    if (!Number.isInteger(position) || position < 1) errors.push(`${label}: column position "${c.position}" must be 1 or more`);
    else if (positions.has(position)) errors.push(`${label}: two columns use position ${position}`);
    positions.add(position);
    if (c.column && !hasColumn(table, c.column)) {
      errors.push(`${label}: column "${c.column}" not found in ${sheet.from} (available: ${columnNames(table).join(", ")})`);
    }
  });
  return errors;
};

const fileKey = (output) => `${(output.fileName || output.id).trim().toLowerCase()}.${output.format === "xlsx" ? "xlsx" : "csv"}`;

// Ids of outputs that would write the same file: same name and format,
// ignoring case.
export const duplicateOutputIds = (workflow) => {
  const counts = {};
  (workflow.outputs || []).forEach((output) => {
    counts[fileKey(output)] = (counts[fileKey(output)] || 0) + 1;
  });
  return new Set((workflow.outputs || []).filter((output) => counts[fileKey(output)] > 1).map((output) => output.id));
};

// Turns run results into file contents.
// Returns { files, errors, errorsById } (errorsById: { [outputId]: [message] }).
// Checks every output and builds each sheet with `build(sheet, table)`.
const eachOutput = (workflow, tables, build) => {
  const files = [];
  const errors = [];
  const errorsById = {};
  const duplicates = duplicateOutputIds(workflow);
  (workflow.outputs || []).forEach((output) => {
    const sheets = sheetsOf(output);
    const label = output.fileName || output.id;
    const outputErrors = sheets.flatMap((sheet) => validateSheet(sheet, tables[sheet.from], `${label} › ${sheet.name || "Sheet1"}`, workflow));
    if (duplicates.has(output.id)) {
      outputErrors.unshift(`${label}: another output has the same file name. Give each output its own name.`);
    }
    if (outputErrors.length) {
      errors.push(...outputErrors);
      errorsById[output.id] = outputErrors;
      return;
    }
    files.push({
      id: output.id,
      fileName: output.fileName || output.id,
      format: output.format || "csv",
      dateFormat: output.dateFormat,
      sheets: (output.format === "xlsx" ? sheets : sheets.slice(0, 1)).map((sheet) => ({
        ...build(sheet, tables[sheet.from]),
        sourceLabel: tableLabel(workflow, sheet.from),
      })),
    });
  });
  return { files, errors, errorsById };
};

export const renderOutputs = (workflow, tables) => eachOutput(workflow, tables, buildSheet);

// Same checks as renderOutputs, without building rows: each sheet is
// { name, from, rowCount, columnCount }. `tables` only need { columns, rowCount }
// (or rows), so the UI can show outputs while the data stays in a worker.
export const describeOutputs = (workflow, tables) =>
  eachOutput(workflow, tables, (sheet, table) => ({
    name: sheet.name || "Sheet1",
    from: sheet.from,
    rowCount: table.rowCount ?? table.rows.length,
    columnCount: sheetColumns(sheet, table).reduce((max, c) => Math.max(max, Number(c.position)), 0),
  }));

const csvCell = (value, dateFormat) => {
  if (isBlank(value)) return "";
  if (isValidDate(value)) return formatDate(value, dateFormat || "yyyy-MM-dd");
  return value;
};

export const toCsv = (file) =>
  Papa.unparse(file.sheets[0].rows.map((row) => row.map((value) => csvCell(value, file.dateFormat))));

export const toXlsxBuffer = async (file) => {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  file.sheets.forEach((sheet, index) => {
    const worksheet = workbook.addWorksheet(uniqueSheetName(sheet.name, workbook, index));
    sheet.rows.forEach((row) => worksheet.addRow(row.map((v) => (isValidDate(v) ? toExcelDate(v) : v ?? null))));
    if (file.dateFormat) {
      worksheet.eachRow((row) => row.eachCell((cell) => {
        if (cell.value instanceof Date) cell.numFmt = file.dateFormat;
      }));
    }
  });
  return workbook.xlsx.writeBuffer();
};

// Excel sheet names: max 31 chars, no []:*?/\ and unique.
const uniqueSheetName = (name, workbook, index) => {
  const base = String(name || `Sheet${index + 1}`).replace(/[[\]:*?/\\]/g, " ").slice(0, 31).trim() || `Sheet${index + 1}`;
  let candidate = base;
  let n = 2;
  while (workbook.getWorksheet(candidate)) candidate = `${base.slice(0, 28)} ${n++}`;
  return candidate;
};

export const fileExtension = (file) => (file.format === "xlsx" ? "xlsx" : "csv");

export const outputFileName = (file) => {
  const ext = fileExtension(file);
  return file.fileName.toLowerCase().endsWith(`.${ext}`) ? file.fileName : `${file.fileName}.${ext}`;
};
